import type { Module } from 'ag-grid-community';
import {
  _ColumnMoveModule,
  _EditCoreModule,
  _HorizontalResizeModule,
  CellApiModule,
  CellStyleModule,
  CheckboxEditorModule,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  ColumnApiModule,
  ColumnAutoSizeModule,
  ColumnHoverModule,
  CustomEditorModule,
  DateEditorModule,
  DragAndDropModule,
  EventApiModule,
  LargeTextEditorModule,
  LocaleModule,
  ModuleRegistry,
  NumberEditorModule,
  NumberFilterModule,
  PinnedRowModule,
  QuickFilterModule,
  RenderApiModule,
  RowApiModule,
  RowSelectionModule,
  RowStyleModule,
  ScrollApiModule,
  SelectEditorModule,
  TextEditorModule,
  TextFilterModule,
  UndoRedoEditModule,
  ValidationModule,
} from 'ag-grid-community';

export function mergeAgGridModules(...moduleLists: (readonly Module[] | Module | undefined | null)[]): Module[] {
  const seen = new Set<Module>();
  const out: Module[] = [];
  for (const listOrOne of moduleLists) {
    if (listOrOne == null) continue;
    const list: readonly Module[] = Array.isArray(listOrOne) ? listOrOne : [listOrOne];
    for (const mod of list) {
      if (seen.has(mod)) continue;
      seen.add(mod);
      out.push(mod);
    }
  }
  return out;
}

export function applyAgGridModuleOverlay(
  base: readonly Module[],
  options?: { enableModules?: readonly Module[] | null; disableModules?: readonly Module[] | null }
): Module[] {
  const withEnabled = mergeAgGridModules(base, options?.enableModules);
  const drop = options?.disableModules;
  if (drop == null || drop.length === 0) return withEnabled;
  const dropSet = new Set<Module>(drop);
  return withEnabled.filter((m) => !dropSet.has(m));
}

export const agGridCoreModules: Module[] = [
  ClientSideRowModelModule,
  ClientSideRowModelApiModule,
  ColumnApiModule,
  CellApiModule,
  RowApiModule,
  EventApiModule,
  RenderApiModule,
  ScrollApiModule,
  LocaleModule,
];

export const agGridColumnUiModules: Module[] = [
  _HorizontalResizeModule,
  _ColumnMoveModule,
  ColumnAutoSizeModule,
  DragAndDropModule,
  ColumnHoverModule,
];

export const agGridFilterModules: Module[] = [TextFilterModule, NumberFilterModule, QuickFilterModule];

export const agGridRowSelectionModules: Module[] = [RowSelectionModule];

export const agGridPinnedRowModules: Module[] = [PinnedRowModule];

export const agGridRowCellStyleModules: Module[] = [RowStyleModule, CellStyleModule];

export const agGridEditingModules: Module[] = [
  _EditCoreModule,
  TextEditorModule,
  NumberEditorModule,
  LargeTextEditorModule,
  DateEditorModule,
  SelectEditorModule,
  CheckboxEditorModule,
  CustomEditorModule,
  UndoRedoEditModule,
  ValidationModule,
];

export const agGridDefaultTableModules: Module[] = mergeAgGridModules(agGridCoreModules, _HorizontalResizeModule);

export const agGridNormalTableModules: Module[] = mergeAgGridModules(
  agGridCoreModules,
  agGridColumnUiModules,
  agGridFilterModules,
  agGridPinnedRowModules,
  agGridRowCellStyleModules
);

export const agGridAllAppModules: Module[] = mergeAgGridModules(
  agGridNormalTableModules,
  agGridEditingModules,
  agGridRowSelectionModules
);

ModuleRegistry.registerModules(agGridAllAppModules);

export const agGridCommunityModules: Module[] = agGridNormalTableModules;

export const agGridModulesRegistered = true;
